// Global Instructions Rule Applied!
//
// Recompute on a capital call that carries manual edits (spec A3.12 part 2,
// owner 2026-10-03). Recomputing is its own step and never discards a
// person's amounts without asking: the dialog lists every manual edit
// (investor, line, override, calculated) and offers
//
//   [Discard manual edits]  [Cancel]  [Keep manual edits]
//
// Discard is destructive and sits leftmost; Keep is the primary action, the
// rightmost button and the default (it holds the focus, so Enter keeps).
// A third action is genuinely needed here, hence the custom footer
// (UnlinkOrDeleteRoundModal precedent). The caller runs the compute:
// Keep = cc__compute_call_items, Discard = cc__clear_overrides first.
//
// A call without manual edits never opens this dialog - Recompute runs
// straight away.

import React from 'react'
import { Button, Modal, Table, Typography } from 'antd'

const { Text } = Typography

export interface ManualEdit {
  /** the per-commitment row id */
  key: string
  investor: string | null
  line: string
  override: number
  calculated: number | null
}

interface RecomputeEditsModalProps {
  open: boolean
  edits: ManualEdit[]
  currencyText: (value: number | null | undefined) => string
  onKeep: () => void
  onDiscard: () => void
  onCancel: () => void
}

export default function RecomputeEditsModal({
  open,
  edits,
  currencyText,
  onKeep,
  onDiscard,
  onCancel
}: RecomputeEditsModalProps): React.ReactElement {
  const one = edits.length === 1
  return (
    <Modal
      title='Recompute: keep the manual edits?'
      open={open}
      onCancel={onCancel}
      width={720}
      destroyOnHidden
      footer={[
        <Button key='discard' danger onClick={onDiscard}>
          Discard manual edits
        </Button>,
        <Button key='cancel' onClick={onCancel}>
          Cancel
        </Button>,
        <Button key='keep' type='primary' autoFocus onClick={onKeep}>
          Keep manual edits
        </Button>
      ]}
    >
      <p className='mb-2'>
        Recompute recalculates the amounts from the call&apos;s inputs.{' '}
        <Text strong>
          {edits.length} amount{one ? ' was' : 's were'} set by hand
        </Text>
        :
      </p>
      <Table
        size='small'
        pagination={false}
        rowKey='key'
        dataSource={edits}
        scroll={edits.length > 8 ? { y: 300 } : undefined}
        columns={[
          { title: 'Investor', dataIndex: 'investor', render: (v: string | null) => v ?? '-' },
          { title: 'Line', dataIndex: 'line' },
          {
            title: 'Override',
            dataIndex: 'override',
            width: 150,
            align: 'right' as const,
            render: (v: number) => <Text strong>{currencyText(v)}</Text>
          },
          {
            title: 'Calculated',
            dataIndex: 'calculated',
            width: 150,
            align: 'right' as const,
            render: (v: number | null) => currencyText(v)
          }
        ]}
      />
      <p className='mt-3 mb-0'>
        <Text strong>Keep</Text> recalculates the other amounts and leaves {one ? 'this one' : 'these'} as
        entered. <Text strong>Discard</Text> clears {one ? 'it' : 'them'} first, so every investor gets the
        calculated amount.
      </p>
    </Modal>
  )
}
